import React, { ReactNode, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import classNames from 'classnames';

import { translate } from '@micro-apps/env';
import './Modal.scss';
import CloseIcon from '../cart/Icons/CloseIcon/index';

interface RenderProps {
	scrollToTop: () => void;
}

interface Props {
	close: () => void;
	className?: string;
	allowEscapeKeyToClose?: boolean;
	allowOutsideClickToClose?: boolean;
	children: ReactNode | (({ scrollToTop }: RenderProps) => ReactNode);
}

export default function Modal({
	close,
	className,
	allowEscapeKeyToClose = true,
	allowOutsideClickToClose = true,
	children,
}: React.PropsWithChildren<Props>) {
	const ref = useRef<HTMLDivElement>(null);

	const handleOverlayClick = evt => {
		if (!allowOutsideClickToClose) {
			return;
		}
		if (ref.current && !ref.current.contains(evt.target)) {
			close();
		}
	};

	const scrollToTop = () => {
		ref?.current?.scrollTo({
			top: 0,
			left: 0,
			behavior: 'auto',
		});
	};

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.removeProperty('overflow');
		};
	}, []);

	useEffect(() => {
		function handleKeyDown(e) {
			if (e.key === 'Escape') {
				close();
			}
		}

		if (allowEscapeKeyToClose) {
			document.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [allowEscapeKeyToClose]);

	return (
		<FocusLock returnFocus>
			<div className="modal-overlay" onClick={handleOverlayClick}>
				<div ref={ref} className={classNames('modal', className)}>
					<button type="button" className="modal__close modal__close-icon" onClick={close}>
						<CloseIcon />
						<span className="Button-label">{translate('Close')}</span>
					</button>
					<div className="modal__content">
						{typeof children === 'function' ? children({ scrollToTop }) : children}
					</div>
				</div>
			</div>
		</FocusLock>
	);
}
